import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle, Instagram, Facebook, Linkedin } from 'lucide-react';
import { buildWhatsAppUrl, trackWhatsAppClick } from '@/lib/whatsapp';
import WhatsAppIntentDialog from '@/components/shared/WhatsAppIntentDialog';

const WHATSAPP_NUMBER = '61481865742';
// Default URL (used by the social-icon link — a single-click shortcut with
// the generic intent-capture template). Context-aware routing is handled by
// the WhatsAppIntentDialog wrapping the "WhatsApp Us" contact row.
const WHATSAPP_LINK = buildWhatsAppUrl({ variant: 'default' });

// Brand social links — update in one place to change everywhere
const SOCIAL_LINKS = [
  {
    name: 'WhatsApp',
    href: WHATSAPP_LINK,
    // Inline WhatsApp glyph (lucide has no brand icon for it)
    icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        {...props}
      >
        <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.89c0 2.09.55 4.13 1.59 5.93L0 24l6.35-1.67a11.88 11.88 0 0 0 5.71 1.45h.01c6.56 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.17-3.44-8.41ZM12.06 21.6h-.01a9.72 9.72 0 0 1-4.95-1.36l-.36-.21-3.77.99 1-3.67-.23-.38a9.69 9.69 0 0 1-1.48-5.08c0-5.37 4.37-9.74 9.8-9.74 2.62 0 5.08 1.02 6.93 2.87a9.72 9.72 0 0 1 2.87 6.92c0 5.37-4.37 9.66-9.8 9.66Zm5.6-7.29c-.31-.15-1.82-.9-2.1-1-.28-.1-.49-.15-.69.15s-.79 1-.97 1.2c-.18.2-.36.23-.67.08-.31-.15-1.3-.48-2.48-1.52a9.33 9.33 0 0 1-1.72-2.14c-.18-.31-.02-.48.14-.63.14-.14.31-.36.46-.54.15-.18.2-.31.31-.51.1-.2.05-.38-.03-.54-.08-.15-.69-1.66-.95-2.27-.25-.6-.5-.52-.69-.53l-.59-.01c-.2 0-.54.08-.82.38-.28.31-1.08 1.05-1.08 2.57 0 1.51 1.1 2.97 1.26 3.17.15.2 2.18 3.33 5.29 4.67.74.32 1.32.51 1.77.65.74.24 1.42.2 1.95.12.59-.09 1.82-.74 2.08-1.46.26-.72.26-1.33.18-1.46-.08-.13-.28-.2-.59-.35Z" />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/winhouseconsultancy?igsh=emo0dnNmN2hnbWVu',
    icon: Instagram,
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/share/1EMEt5VjP5/',
    icon: Facebook,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/winhouse-consultancy/',
    icon: Linkedin,
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@winhouse_consultancy?_r=1&_t=ZS-95lX4dL6RzY',
    // Inline TikTok glyph
    icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        {...props}
      >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.1 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.14-.1Z" />
      </svg>
    ),
  },
];

const Footer = () => {
  const navigate = useNavigate();
  
  const scrollToSection = (sectionId) => {
    // Check if we're on the homepage
    if (window.location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to home first, then scroll
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };
  
  return (
    <footer style={{ background: 'var(--primary-black)' }} className="text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <img
              src="/winhouse-logo-footer.png"
              alt="Winhouse Tutors"
              className="logo-footer-white mb-5"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              Premium tutoring for IELTS, VCE, GMAT, GRE, SAT, and PTE worldwide.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-5 text-base" style={{ color: 'var(--accent-gold)' }}>Services</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li>
                <button 
                  onClick={() => scrollToSection('services')} 
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block text-left"
                >
                  IELTS Coaching
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')} 
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block text-left"
                >
                  VCE Tutoring
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('pricing')} 
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block text-left"
                >
                  Pricing
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-5 text-base" style={{ color: 'var(--accent-gold)' }}>For Tutors</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li>
                <Link to="/become-tutor" className="hover:text-white transition-colors hover:translate-x-1 inline-block">
                  Become a Tutor
                </Link>
              </li>
              <li>
                <Link to="/become-tutor" className="hover:text-white transition-colors hover:translate-x-1 inline-block">
                  Apply Now
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-5 text-base" style={{ color: 'var(--accent-gold)' }}>Company</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li>
                <button 
                  onClick={() => scrollToSection('testimonials')} 
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block text-left"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('faq')} 
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block text-left"
                >
                  FAQ
                </button>
              </li>
              <li>
                <Link to="/get-started" className="hover:text-white transition-colors hover:translate-x-1 inline-block">
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/pay"
                  data-testid="footer-make-a-payment-link"
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block"
                >
                  Make a Payment
                </Link>
              </li>
              <li>
                <Link
                  to="/donate"
                  data-testid="footer-donate-link"
                  className="hover:text-white transition-colors hover:translate-x-1 inline-block"
                >
                  Donate to Support a Student
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-5 text-base" style={{ color: 'var(--accent-gold)' }}>Contact</h4>
            <ul className="space-y-4 text-sm text-gray-300">
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <a href="mailto:info@winhousetutors.com" className="hover:text-white transition-colors">
                  info@winhousetutors.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <a href="tel:+61481865742" className="hover:text-white transition-colors">
                  +61 481 865 742
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <WhatsAppIntentDialog source="footer-contact">
                  <button
                    type="button"
                    className="hover:text-white transition-colors text-left"
                    data-testid="footer-whatsapp-contact"
                  >
                    WhatsApp Us
                  </button>
                </WhatsAppIntentDialog>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4" style={{ color: 'var(--accent-teal)' }} />
                <span>Worldwide (Online)</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 pt-8 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-6 text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Winhouse Tutors. All rights reserved.</p>
          <div
            className="flex items-center gap-3 md:gap-4"
            data-testid="footer-social-icons"
            aria-label="Follow Winhouse Tutors on social media"
          >
            <span className="uppercase tracking-wider text-xs text-gray-500 hidden sm:inline">
              Follow us
            </span>
            {SOCIAL_LINKS.map(({ name, href, icon: Icon }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                title={name}
                data-testid={`footer-social-${name.toLowerCase()}`}
                onClick={() => {
                  if (name === 'WhatsApp') {
                    trackWhatsAppClick('footer-social-icon', 'default');
                  }
                }}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-800 text-gray-400 hover:text-[color:var(--accent-gold)] hover:border-[color:var(--accent-gold)] hover:scale-110 transition-all duration-200"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
